import { LIFECYCLE_STAGES } from '@app/components/lifecycle/lifecycle-stages';
import { roundNumberToNDecimalPlaces } from '@app/utils/helpers';
import { ProductResult } from '@shared/api/types';
import SimplePieChart from '@shared/components/chart/simple-pie-chart';
import { CustomTooltipProps } from '@shared/components/chart/tooltips/types';
import SmallNumber from '@shared/components/content/small-number';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import { ChartConfig } from '@shared/components/ui/chart';
import { cn } from '@shared/lib/utils';
import React from 'react';

function normalizeLifecycleStage(stage: string) {
  return stage.toLowerCase().replace(/[\s_-]/g, '');
}

function toGco2e(valueInKgCo2e: number) {
  return valueInKgCo2e * 1000;
}

function LifecycleStageTooltip({
  value,
  name,
  item,
  chartData,
}: CustomTooltipProps) {
  const total =
    chartData?.reduce((acc, d) => acc + ((d['emissions'] as number) || 0), 0) ||
    1;

  const percent = (((value as number) || 0) / total) * 100;
  const fill =
    (chartData?.find((d) => d['stage'] === name)?.['fill'] as string) ??
    'currentColor';

  const payload = item.payload as {
    productEmissions: number;
    ingredientEmissions: number;
  };
  const stageName = (name as string).toLowerCase();

  return (
    <div>
      <span>{name}</span>
      <div className="mt-1 flex flex-1 items-center gap-2">
        <div
          className="size-2.5 shrink-0 rounded-[2px]"
          style={{ backgroundColor: fill }}
        />
        <div className="flex items-baseline gap-0.5 font-mono font-medium tabular-nums text-foreground">
          <SmallNumber value={(value as number) / 1000} />
          <Unit variant="kgCO2e" className="ml-1" />
          <span className="ml-2 font-normal text-muted-foreground">
            ({roundNumberToNDecimalPlaces(percent, 0, false)}%)
          </span>
        </div>
      </div>
      {(payload.productEmissions > 0 || payload.ingredientEmissions > 0) && (
        <div className="mt-1.5 space-y-0.5 pl-4 text-xs text-muted-foreground">
          {payload.productEmissions > 0 && (
            <div className="flex justify-between gap-4">
              <span>
                {name === 'Farming'
                  ? 'Ingredient farming'
                  : `Your ${stageName}`}
              </span>
              <span className="font-mono">
                <SmallNumber value={payload.productEmissions / 1000} />
              </span>
            </div>
          )}
          {payload.ingredientEmissions > 0 && (
            <div className="flex justify-between gap-4">
              <span>Ingredient {stageName}</span>
              <span className="font-mono">
                <SmallNumber value={payload.ingredientEmissions / 1000} />
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface ProductDetailLifecycleStageEmissionsSectionProps {
  product: ProductResult;
  pdf?: boolean;
}

export default function ProductDetailLifecycleStageEmissionsSection({
  product,
  pdf = false,
}: ProductDetailLifecycleStageEmissionsSectionProps) {
  const emissionsByStage = React.useMemo(() => {
    const map = new Map<string, number>();

    for (const item of product.lifeCycleStageEmissions || []) {
      if (!item?.lifeCycleStage) continue;
      const normalized = normalizeLifecycleStage(item.lifeCycleStage);
      const grams = toGco2e(item.emissions ?? 0);
      map.set(normalized, grams);
    }

    return map;
  }, [product.lifeCycleStageEmissions]);

  const stageBreakdowns = React.useMemo(
    () => ({
      Farming: {
        productEmissions: emissionsByStage.get('farming') ?? 0,
        ingredientEmissions: 0,
      },
      Processing: {
        productEmissions: toGco2e(product.processing.processingEmissions),
        ingredientEmissions: toGco2e(
          product.ingredientProcessingEmissions ?? 0
        ),
      },
      Packaging: {
        productEmissions: toGco2e(product.packaging.packagingEmissions),
        ingredientEmissions: toGco2e(product.ingredientPackagingEmissions ?? 0),
      },
      Transport: {
        productEmissions: toGco2e(product.transport.transportEmissions),
        ingredientEmissions: toGco2e(product.ingredientTransportEmissions ?? 0),
      },
    }),
    [product, emissionsByStage]
  );

  const grandTotalKg = product.totalEmissions ?? 0;

  const chartData = React.useMemo(() => {
    return [
      {
        stage: 'Farming',
        emissions: stageBreakdowns.Farming.productEmissions,
        productEmissions: stageBreakdowns.Farming.productEmissions,
        ingredientEmissions: 0,
        fill: 'var(--farming-default)',
      },
      {
        stage: 'Processing',
        emissions:
          stageBreakdowns.Processing.productEmissions +
          stageBreakdowns.Processing.ingredientEmissions,
        productEmissions: stageBreakdowns.Processing.productEmissions,
        ingredientEmissions: stageBreakdowns.Processing.ingredientEmissions,
        fill: 'var(--processing-default)',
      },
      {
        stage: 'Packaging',
        emissions:
          stageBreakdowns.Packaging.productEmissions +
          stageBreakdowns.Packaging.ingredientEmissions,
        productEmissions: stageBreakdowns.Packaging.productEmissions,
        ingredientEmissions: stageBreakdowns.Packaging.ingredientEmissions,
        fill: 'var(--packaging-default)',
      },
      {
        stage: 'Transport',
        emissions:
          stageBreakdowns.Transport.productEmissions +
          stageBreakdowns.Transport.ingredientEmissions,
        productEmissions: stageBreakdowns.Transport.productEmissions,
        ingredientEmissions: stageBreakdowns.Transport.ingredientEmissions,
        fill: 'var(--transport-default)',
      },
    ]
      .reverse()
      .filter(({ emissions }) => emissions > 0);
  }, [stageBreakdowns]);

  const chartConfig = React.useMemo(
    () =>
      ({
        Farming: {
          label: 'Farming',
          color: 'var(--farming-default)',
          unit: 'gCO2e',
        },
        Processing: {
          label: 'Processing',
          color: 'var(--processing-default)',
          unit: 'gCO2e',
        },
        Packaging: {
          label: 'Packaging',
          color: 'var(--packaging-default)',
          unit: 'gCO2e',
        },
        Transport: {
          label: 'Transport',
          color: 'var(--transport-default)',
          unit: 'gCO2e',
        },
      }) as ChartConfig,
    []
  );

  if (!product.lifeCycleStageEmissions?.length) {
    return null;
  }

  return (
    <div>
      <div className="pb-4">
        <Text
          variant="cardTitle"
          className={cn('text-primary-700', {
            'font-heading text-md font-bold': pdf,
          })}
        >
          Emissions by lifecycle stage
        </Text>
        <Text
          variant="subtle"
          className={cn('mt-1 text-sm', {
            'text-xs': pdf,
          })}
        >
          Lifecycle stages break down the emissions of a product by where they
          occur in the product&apos;s journey.{' '}
          {!pdf && (
            <>
              Our assessments cover the emissions from cradle-to-store, which
              includes the ingredients, any processing or packaging you add to
              create the final product, as well as any transportation to store.
            </>
          )}
        </Text>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[3fr_2fr]">
        <div className="space-y-3">
          {LIFECYCLE_STAGES.map(
            ({ stage, description, icon: Icon, borderClassName }) => {
              const { productEmissions, ingredientEmissions } =
                stageBreakdowns[stage];
              const totalEmissions = productEmissions + ingredientEmissions;
              const totalEmissionsKg = totalEmissions / 1000;
              const percent =
                grandTotalKg > 0 ? (totalEmissionsKg / grandTotalKg) * 100 : 0;

              return (
                <div
                  key={stage}
                  className={cn(
                    'flex gap-3 rounded-md border border-l-[12px] bg-gray-00 px-3 py-2',
                    borderClassName
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center space-x-2">
                        <Icon className="text-primary-800" size={16} />
                        <Text className="font-medium">{stage}</Text>
                      </div>
                      <div className="flex shrink-0 items-baseline gap-2 text-sm">
                        <div className="flex items-baseline justify-end gap-1">
                          <SmallNumber value={totalEmissionsKg} />
                          <Unit variant="kgCO2e" className=" text-xs" />
                        </div>
                        {/* <div className="h-full w-px bg-gray-500" /> */}
                        <div className="flex min-w-20 items-baseline justify-end gap-0.5">
                          {roundNumberToNDecimalPlaces(percent, 0, false)}
                          <Unit variant="% of total" className="text-xs" />
                        </div>
                      </div>
                    </div>
                    {!pdf && (
                      <Text variant="subtle" className="mt-1 text-xs">
                        {description}
                      </Text>
                    )}
                  </div>
                </div>
              );
            }
          )}
        </div>

        <div className="flex items-center justify-center">
          <SimplePieChart
            nameKey="stage"
            dataKey="emissions"
            chartData={chartData}
            chartConfig={chartConfig}
            innerRadius={pdf ? 45 : 60}
            tooltipFormatter={LifecycleStageTooltip}
            className={cn('size-[260px]', {
              'size-[200px]': pdf,
            })}
          />
        </div>
      </div>
    </div>
  );
}
